  <template>
    <!-- Loading -->
    <div class="vld-parent">
      <loading
        v-model:active="isLoading"
        :is-full-page="true"
        :height="128"
        :width="128"
      />
    </div>

    <!-- Form -->
    <form @submit.prevent="store" class="form-content">
      <div class="form-content-inner">
        <control
          type="hidden"
          v-model="employeecontractfile.id"
          :hasError="!!errors.id"
          id="id"
        />

        <div class="form-col">
          <field
            :label="$t('employeecontractfile.name')"
            class="w-full md:mr-2 mb-2 md:mb-0"
            :hasError="errors.name"
          >
            <control
              type="text"
              :placeholder="$t('employeecontractfile.name')"
              v-model="employeecontractfile.name"
              :hasError="!!errors.name"
              id="name"
              required="required"
            />
          </field>
        </div>

        <div class="form-col">
          <field
            :label="$t('employeecontractfile.file')"
            class="w-full"
            :hasError="errors.file"
          >
            <input
              type="file"
              accept="application/pdf"
              v-on:change="onFileChange"
              class="form-control-file"
              id="my-file"
              required="required"
            />
          </field>
        </div>

        <div class="border p-2 mt-3 mb-6" v-if="preview || image.name">
          <p>Preview Information:</p>
          <div>
            <p class="mb-0">File name: {{ image.name }}</p>
            <p class="mb-0">Size: {{ image.size / 1024 }}KB</p>
          </div>
        </div>

        <div class="preview-content">
          <div class="outer-prev">
            <h2 class="preview-title">File Preview</h2>
            <center><p v-if="!previewFile">No Preview</p></center>
            <vue-pdf-embed
              :source="previewFile"
              style="width: 100%; height: 100%; min-height: 500px;"
            />
          </div>
        </div>

        <div
          class="buttons-submit transition-colors duration-1000"
          :class="darkMode ? 'bg-darkmode-base' : 'bg-gray-100'"
        >
          <jb-buttons class="buttons-action-submit">
            <jb-button
              type="submit"
              color="info"
              :icon="mdiCheckBold"
              :label="$t('finance.button.btnSubmit')"
              class="form-submit-btn"
            />
            <jb-button
              type="button"
              color="info"
              outline
              :label="$t('finance.button.btnReset')"
              class="form-noicon-btn btn-reset"
              @click="reset()"
            />
          </jb-buttons>
        </div>
      </div>
    </form>
  </template>

<script>
/* eslint-disable */
import { ref, reactive, onMounted, watch, watchEffect } from "vue"
import axios from "axios"
import { mdiBallot, mdiCheckBold } from "@mdi/js"
import Control from "@/components/Control"
import JbButton from "@/components/JbButton"
import JbButtons from "@/components/JbButtons"
import Field from "@/components/Field"
import { useToast } from "vue-toastification"
import { useStore } from "vuex"
import { useRouter } from "vue-router"
import Loading from "vue-loading-overlay"
import "vue-loading-overlay/dist/vue-loading.css"
import VuePdfEmbed from "vue-pdf-embed"

export default {
  name: "EmployeeContractFile",
  components: {
    Field,
    Control,
    Loading,
    JbButton,
    JbButtons,
    VuePdfEmbed
  },
  props: {
    showEdit: {
      type: Boolean,
      default: false,
    },
    propEmployeeContractFile: {
      type: Object,
      default: () => ({ empty: true }),
    },
    employeeContractId: {
      type: String,
      default: "",
    },
    employeeId: {
      type: String,
      default: "",
    },
  },
  emits: ["close-modal", "submit", "reset-modal"],
  setup(props, { emit }) {
    const titleStack = ref(["Admin", "Forms"])
    const isLoading = ref(false)
    const toast = useToast()
    const router = useRouter()
    const errors = ref({})
    const str = useStore()
    const userlogin = localStorage.getItem("name")
    const preview = ref("")
    const previewFile = ref(null)
    const image = ref({ name: "", size: 0 })

    const employeeContractList = ref([])

    const employeecontractfile = reactive({
      id: null,
      employee_id: "",
      employee_contract_id: "",
      name: "",
      file: "",
      created_by: "",
      updated_by: "",
    })

    const reset = () => {
      employeecontractfile.id = ""
      employeecontractfile.employee_id = ""
      employeecontractfile.employee_contract_id = ""
      employeecontractfile.name = ""
      employeecontractfile.file = ""
      employeecontractfile.created_by = ""
      employeecontractfile.updated_by = ""
      preview.value = ""
      image.value = { name: "", size: 0 }
      errors.value = {}
    }

    const store = async () => {
      isLoading.value = true
      const url = props.showEdit
        ? `/employeecontract-file/${employeecontractfile.id}`
        : "/employeecontract-file"
      const method = props.showEdit ? "PUT" : "POST"

      if (method === "POST") {
        employeecontractfile.created_by = userlogin
      } else {
        employeecontractfile.updated_by = userlogin
      }

      employeecontractfile.employee_contract_id = props.employeeContractId
      employeecontractfile.employee_id = props.employeeId

      try {
        const response = await axios({
          method,
          url,
          data: employeecontractfile
        })

        if (response.data.success) {
          emit("reset-modal")
          emit("close-modal")
          toast.success(response.data.message)
        } else {
          errors.value = response.data.errors || response.data.errors
          toast.error(response.data.message || "Failed to save data")
        }
      } catch (error) {
        if (error.response) {
          errors.value = error.response.data.errors || {}
          toast.error(error.response.data.message || "Error occurred")
          console.log("Error response:", JSON.stringify(error.response.data))
        } else if (error.message) {
          toast.error(error.message)
          console.log("Error message:", JSON.stringify(error.message))
        }
      } finally {
        isLoading.value = false
        if (!props.showEdit) reset()
      }
    }

    const getEmployeeContract = async () => {
      isLoading.value = true
      await axios
        .get("employeecontract")
        .then((response) => {
          employeeContractList.value = response.data.data
          isLoading.value = false
        })
        .catch((error) => {
          toast.error(error.response.data, { timeout: 5000 })
          console.log(error)
          isLoading.value = false
        })
    }

    const MaxUploadLimit = ref(0)
    const getUploadLimitEmployeeContractFile = async () => {
      await axios.get("/upload-limit-employeecontract-file").then((res) => {
        MaxUploadLimit.value = res.data.data
      })
    }

    const onFileChange = async (event) => {
      const file = event.target.files[0]
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          toast.error("File too big (> 5MB)")
          event.target.value = ""
          preview.value = ""
          previewFile.value = null
          image.value = { name: "", size: 0 }
          employeecontractfile.file = ""
          return
        }

        if (file.type !== "application/pdf") {
          toast.error("Only PDF files are allowed")
          event.target.value = ""
          preview.value = ""
          previewFile.value = null
          image.value = { name: "", size: 0 }
          employeecontractfile.file = ""
          return
        }

        if (previewFile.value) {
          URL.revokeObjectURL(previewFile.value)
        }
        previewFile.value = URL.createObjectURL(file)

        const reader = new FileReader()
        reader.onload = (e) => {
          preview.value = e.target.result
          employeecontractfile.file = e.target.result
          image.value = {
            name: file.name,
            size: file.size,
          }
        }
        reader.onerror = () => {
          toast.error("Failed to read file")
          preview.value = ""
          image.value = { name: "", size: 0 }
          employeecontractfile.file = ""
        }
        reader.readAsDataURL(file)
      } else {
        preview.value = ""
        image.value = { name: "", size: 0 }
        employeecontractfile.file = ""
      }
    }

    onMounted(() => {
      getEmployeeContract()
      getUploadLimitEmployeeContractFile()
    })

    watchEffect(() => {
      if (props.employeeContractId) {
        employeecontractfile.employee_contract_id = props.employeeContractId
      }

      if (props.employeeId) {
        employeecontractfile.employee_id = props.employeeId
      }

      if (props.showEdit && props.propEmployeeContractFile?.id) {
        Object.assign(employeecontractfile, props.propEmployeeContractFile)
      }
    })



    return {
      isLoading,
      titleStack,
      employeecontractfile,
      errors,
      mdiBallot,
      mdiCheckBold,
      employeeContractList,
      getEmployeeContract,
      store,
      reset,
      router,
      toast,
      userlogin,
      str,
      preview,
      image,
      onFileChange,
      MaxUploadLimit,
      previewFile
    }
  },
  methods: {
    clearForm() {
      this.employeecontractfile = reactive({
        id: "",
        employee_id: "",
        employee_contract_id: "",
        name: "",
        file: "",
        created_by: "",
        updated_by: "",
      })
      this.preview = ""
      this.image = { name: "", size: 0 }
    },
    // onChange: function (employee_contract_id, event) {
    //     if (event.target.value) {
    //         employee_contract_id = event.target.value
    //     }
    // }
  },
}
</script>
